import {
  createPixelArtFromUpload,
  type UploadPixelArtResult,
} from "@/utils/api";
import { renderPatternToDataUrl } from "@/utils/patternRenderer";
import { useSpeechRecognition } from "@/utils/speechRecognition";
import {
  AudioOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InboxOutlined,
  PictureOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { UploadProps } from "antd";
import {
  Alert,
  Button,
  Card,
  Grid,
  Image,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "tui-color-picker/dist/tui-color-picker.css";
import ImageEditor from "tui-image-editor";
import "tui-image-editor/dist/tui-image-editor.css";

type ColorMode = "easy" | "medium" | "hard";
type GenerationMode = "text" | "direct" | "ai_image" | "ai_image_text";
type ImageModelProvider = "ark" | "qwen";

type UploadedImage = {
  dataUri: string;
  filename: string;
  width: number;
  height: number;
  newUpload: boolean;
};

const MIN_SIZE = 50;
const MAX_SIZE = 120;

function clampSize(value: number) {
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.round(value)));
}

function readFileAsDataUri(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImageSize(src: string) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new window.Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = reject;
    image.src = src;
  });
}

export default function UploadPatternPage() {
  const { t, i18n } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [uploadedImage, setUploadedImage] = useState<UploadedImage>();
  const [textPrompt, setTextPrompt] = useState("");
  const [paletteName, setPaletteName] = useState("mard72");
  const [imageModelProvider, setImageModelProvider] =
    useState<ImageModelProvider>("qwen");
  const [patternSize, setPatternSize] = useState(80);
  const [colorMode, setColorMode] = useState<ColorMode>("medium");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<UploadPixelArtResult>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const imageEditorRef = useRef<HTMLDivElement>(null);
  const [imageEditorInstance, setImageEditorInstance] = useState<ImageEditor>();
  const speech = useSpeechRecognition({
    lang: i18n.language?.startsWith("en") ? "en-US" : "zh-CN",
    onText: (text) => {
      setTextPrompt((value) => {
        const trimmed = value.trimEnd();
        return trimmed ? `${trimmed} ${text}` : text;
      });
    },
    onError: () => {
      message.warning(t("speech.error"));
    },
  });

  const colorLimit = useMemo(() => {
    if (colorMode === "easy") return 8;
    if (colorMode === "hard") return 22;
    return 15;
  }, [colorMode]);

  const renderedPatternDataUrl = useMemo(() => {
    if (!result?.patternGrid?.length) return result?.patternImageUrl;
    return renderPatternToDataUrl({
      width: result.width,
      height: result.height,
      grid: result.patternGrid,
      legend: result.legend,
      meta: result.patternMeta,
      title: t("uploadPattern.resultAlt"),
    });
  }, [result, t]);

  const handleFile = async (file: File) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      message.error(t("uploadPattern.unsupportedType"));
      return Upload.LIST_IGNORE;
    }
    setResult(undefined);
    const dataUri = await readFileAsDataUri(file);
    const size = await loadImageSize(dataUri);
    setUploadedImage({
      dataUri,
      filename: file.name,
      width: size.width,
      height: size.height,
      newUpload: true,
    });
    return false;
  };
  useEffect(() => {
    if (uploadedImage?.newUpload) {
      openImageEdit();
    }
  }, [uploadedImage?.dataUri]);
  const openImageEdit = () => {
    setIsModalOpen(true);
    const imageEditor = new ImageEditor(imageEditorRef.current as Element, {
      includeUI: {
        initMenu: "filter",
        menuBarPosition: "bottom",
        loadImage: {
          name: "image",
          path: uploadedImage?.dataUri || "",
        },
      },
      selectionStyle: {
        cornerSize: 20,
        rotatingPointOffset: 70,
      },
    });
    const button_div = document.querySelectorAll(
      ".tui-image-editor-header-buttons",
    )[0] as HTMLDivElement | undefined;
    if (button_div) {
      button_div.style.display = "none";
    }
    const logo_div = document.querySelectorAll(
      ".tui-image-editor-header-logo",
    )[0] as HTMLDivElement | undefined;
    if (logo_div) {
      logo_div.style.display = "none";
    }
    setImageEditorInstance(imageEditor);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };
  const handleSave = async () => {
    const newImageUrl = imageEditorInstance?.toDataURL() || "";
    const newSize = await loadImageSize(newImageUrl);
    setUploadedImage((prev) =>
      prev
        ? {
            ...prev,
            dataUri: newImageUrl,
            width: newSize.width,
            height: newSize.height,
            newUpload: false,
          }
        : prev,
    );
    setIsModalOpen(false);
  };

  const uploadProps: UploadProps = {
    accept: "image/png,image/jpeg,image/webp",
    maxCount: 1,
    showUploadList: false,
    beforeUpload: handleFile,
  };

  const handleSizeChange = (value: number | null) => {
    setPatternSize(clampSize(value || 50));
  };

  const handleGenerate = async (modeOverride?: GenerationMode) => {
    const prompt = textPrompt.trim();
    if (!uploadedImage && !prompt) {
      message.warning(t("uploadPattern.inputRequired"));
      return;
    }
    if (modeOverride === "direct" && !uploadedImage) {
      message.warning(t("uploadPattern.selectImageFirst"));
      return;
    }
    const generationMode: GenerationMode =
      modeOverride === "direct" && uploadedImage
        ? "direct"
        : uploadedImage
          ? prompt
            ? "ai_image_text"
            : "ai_image"
          : "text";
    setLoading(true);
    try {
      const data = await createPixelArtFromUpload({
        imageDataUri: uploadedImage?.dataUri,
        filename: uploadedImage?.filename,
        paletteName,
        imageModelProvider,
        width: patternSize,
        height: patternSize,
        colorLimit,
        generationMode,
        editPrompt: prompt || undefined,
      });
      setResult(data);
      if (data.imageUrl) {
        try {
          const size = await loadImageSize(data.imageUrl);
          setUploadedImage({
            dataUri: data.imageUrl,
            filename: data.pixelArtId
              ? `generated-${data.pixelArtId}.png`
              : "generated-image.png",
            width: size.width,
            height: size.height,
            newUpload: false,
          });
        } catch {
          setUploadedImage({
            dataUri: data.imageUrl,
            filename: data.pixelArtId
              ? `generated-${data.pixelArtId}.png`
              : "generated-image.png",
            width: 0,
            height: 0,
            newUpload: false,
          });
        }
      }
      message.success(t("uploadPattern.generated"));
    } catch (error) {
      message.error(error instanceof Error ? error.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!renderedPatternDataUrl) return;
    const link = document.createElement("a");
    link.href = renderedPatternDataUrl;
    link.download = `bead-pattern-${new Date().toISOString().slice(0, 10)}.png`;
    link.click();
  };
  const handleDownloadOriginal = () => {
    if (!uploadedImage) return;
    const link = document.createElement("a");
    link.href = uploadedImage.dataUri;
    link.download = uploadedImage.filename;
    link.click();
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <Card className="brand-surface-hero shrink-0" variant="outlined">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <Space orientation="vertical" size="small">
            <Tag color="blue">{t("uploadPattern.badge")}</Tag>
            <Typography.Title level={2} className="m-0">
              {t("uploadPattern.title")}
            </Typography.Title>
            <Typography.Paragraph className="m-0 text-slate-600">
              {t("uploadPattern.subtitle")}
            </Typography.Paragraph>
          </Space>
        </div>
      </Card>

      <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto overflow-x-hidden lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <Card className="brand-surface-card" variant="outlined">
          <Space orientation="vertical" size="large" className="w-full">
            <Space orientation="vertical" size="small" className="w-full">
              <Typography.Text strong>
                {t("uploadPattern.textPrompt")}
              </Typography.Text>
              <div className="flex gap-2">
                <Input.TextArea
                  className="flex-1"
                  value={textPrompt}
                  onChange={(event) => setTextPrompt(event.target.value)}
                  placeholder={t("uploadPattern.textPromptPlaceholder")}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  disabled={speech.active}
                />
                <Tooltip
                  title={
                    speech.active
                      ? t("speech.stop")
                      : speech.supported
                        ? t("speech.start")
                        : t("speech.unsupported")
                  }
                >
                  <Button
                    icon={<AudioOutlined />}
                    onClick={speech.toggle}
                    disabled={!speech.supported}
                    type={speech.listening ? "primary" : "default"}
                    className="h-auto"
                  />
                </Tooltip>
              </div>
              {speech.active ? (
                <Alert type="info" showIcon message={t("speech.listening")} />
              ) : null}
            </Space>

            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t("uploadPattern.dropTitle")}</p>
              <p className="ant-upload-hint">{t("uploadPattern.dropHint")}</p>
            </Upload.Dragger>

            {uploadedImage ? (
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <Image
                  src={uploadedImage.dataUri}
                  alt={uploadedImage.filename}
                  className="max-h-80 object-contain"
                  width="100%"
                  preview={{ mask: t("common.preview") }}
                />
                <div className="px-3 py-2 text-sm text-slate-500">
                  {uploadedImage.filename}
                  {uploadedImage.width && uploadedImage.height
                    ? ` · ${uploadedImage.width}×${uploadedImage.height}`
                    : ""}
                </div>
                <div className="w-full flex columns-3 flex-wrap gap-2 justify-center">
                  <Button
                    type="primary"
                    size="large"
                    icon={<EditOutlined />}
                    disabled={!uploadedImage}
                    onClick={openImageEdit}
                  >
                    {t("uploadPattern.editImage")}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<DeleteOutlined />}
                    disabled={!uploadedImage}
                    onClick={() => (
                      setUploadedImage(undefined),
                      setResult(undefined)
                    )}
                  >
                    {t("uploadPattern.removeImage")}
                  </Button>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    size="large"
                    onClick={handleDownloadOriginal}
                  >
                    {t("playground.downloadBtn")}
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <Typography.Text strong className="mb-3 block">
                {t("uploadPattern.settings")}
              </Typography.Text>
              <Space
                className="w-full"
                size="middle"
                orientation={screens.md ? "horizontal" : "vertical"}
                wrap
              >
                <Space orientation="vertical" size="small">
                  <Typography.Text type="secondary">
                    {t("chat.palette")}
                  </Typography.Text>
                  <Select
                    value={paletteName}
                    onChange={setPaletteName}
                    className="min-w-32"
                    options={[
                      { value: "mard72", label: t("chat.palette72") },
                      // { value: "mard96", label: t("chat.palette96") },
                      // { value: "mard120", label: t("chat.palette120") },
                      // { value: "mard144", label: t("chat.palette144") },
                      // { value: "mard221", label: t("chat.palette221") },
                      // { value: "mard295", label: t("chat.palette295") },
                    ]}
                  />
                </Space>
                <Space orientation="vertical" size="small">
                  <Typography.Text type="secondary">
                    {t("modelProvider.label")}
                  </Typography.Text>
                  <Select
                    value={imageModelProvider}
                    onChange={(value) =>
                      setImageModelProvider(value as ImageModelProvider)
                    }
                    className="min-w-32"
                    options={[
                      { value: "qwen", label: t("modelProvider.qwen") },
                      { value: "ark", label: t("modelProvider.ark") },
                    ]}
                  />
                </Space>
                <Space orientation="vertical" size="small">
                  <Typography.Text type="secondary">
                    {t("chat.difficulty", {
                      desc: t(
                        `chat.difficulty${colorMode.charAt(0).toUpperCase() + colorMode.slice(1)}`,
                      ),
                    })}
                  </Typography.Text>
                  <Segmented
                    value={colorMode}
                    onChange={(value) => setColorMode(value as ColorMode)}
                    options={[
                      { value: "easy", label: t("chat.easy") },
                      { value: "medium", label: t("chat.medium") },
                      { value: "hard", label: t("chat.hard") },
                    ]}
                  />
                </Space>
                <Space orientation="vertical" size="small">
                  <Typography.Text type="secondary">
                    {t("uploadPattern.patternSize")}
                  </Typography.Text>
                  <InputNumber
                    min={MIN_SIZE}
                    max={MAX_SIZE}
                    value={patternSize}
                    onChange={handleSizeChange}
                  />
                </Space>
              </Space>
              <Alert
                className="mt-4"
                type="info"
                showIcon
                title={t("uploadPattern.aspectLocked")}
              />
            </div>

            <div className="w-full flex columns-2 flex-wrap gap-2 justify-center">
              <Button
                size="large"
                icon={<UploadOutlined />}
                loading={loading}
                disabled={!uploadedImage}
                onClick={() => handleGenerate("direct")}
              >
                {t("uploadPattern.generateDirect")}
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<UploadOutlined />}
                loading={loading}
                disabled={!uploadedImage && !textPrompt.trim()}
                onClick={() => handleGenerate()}
              >
                {t("uploadPattern.generate")}
              </Button>
            </div>
          </Space>
        </Card>

        <Card className="brand-surface-card min-h-0" variant="outlined">
          {loading ? (
            <div className="flex min-h-96 items-center justify-center">
              <Spin size="large" tip={t("uploadPattern.generating")} />
            </div>
          ) : result ? (
            <Space orientation="vertical" size="middle" className="w-full">
              <Image
                src={renderedPatternDataUrl}
                alt={t("uploadPattern.resultAlt")}
                className="max-h-[58vh] object-contain"
                width="100%"
                preview={{ mask: t("common.preview") }}
              />
              <Space wrap>
                <Tag color="blue">
                  {result.width}×{result.height}
                </Tag>
                <Tag color="green">
                  {t("playground.palette")}: {result.paletteName}
                </Tag>
                <Tag>{result.totalBeads} beads</Tag>
              </Space>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                {result.legend.map((item) => (
                  <div
                    key={item.code}
                    className="rounded border px-2 py-1 text-xs"
                    style={{ backgroundColor: item.hex }}
                  >
                    <span className="font-semibold">{item.code}</span>{" "}
                    {item.count}
                  </div>
                ))}
              </div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
              >
                {t("playground.downloadBtn")}
              </Button>
            </Space>
          ) : (
            <div className="flex min-h-96 flex-col items-center justify-center text-center text-slate-500">
              <PictureOutlined className="mb-3 text-5xl" />
              <Typography.Text>
                {t("uploadPattern.emptyResult")}
              </Typography.Text>
            </div>
          )}
        </Card>
      </div>
      <Modal
        title={t("uploadPattern.editImage")}
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={handleSave}
        width="80vw"
        forceRender
      >
        <div className="h-screen!" ref={imageEditorRef}></div>
      </Modal>
    </div>
  );
}
